import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiResponse } from '../models/api-response';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { API_ENDPOINTS, buildEndpoint } from '../api-endpoints-v2';
import { DecisionAction, ReviewDecision, ReviewSection } from '../models/v2models/review-state';
import {
  ReviewSectionDto,
  mapReviewSections,
  mapStatus,
} from '../report/review-refine/review-api.mapper';
import { ChangeStatus } from '../models/v2models/review-state';
import { ReportPdfType } from '../models/v2models/report-pdf-type';

export interface RecordDecisionRequest {
  sectionId: number;
  changeId: number;
  action: DecisionAction;
  editedHtml: string | null;
}

/** POST /changes/{changeId}/decision response payload. */
interface DecisionResponseDto {
  changed?: number;
  changeId?: number;
  reportId: number;
  sectionId: number;
  status: string;
  editedText: string | null;
  updatedDate: string;
}

export interface DecisionResult {
  changeId: number;
  reportId: number;
  sectionId: number;
  status: ChangeStatus;
  editedHtml: string | null;
  updatedDate: string;
}

/** POST /Reports/{reportId}/edits/apply response payload. */
interface ApplyEditsResponseDto {
  report_id?: number;
  reportId?: number;
}

export interface ApplyEditsResult {
  reportId: number;
}

export type PreviewFileStatus = 'Started' | 'InProgress' | 'Completed';

/** Where the two rendered previews live, and whether generation has finished. */
export interface PreviewFiles {
  priorReportPreviewPath: string | null;
  tempReportPreviewPath: string | null;
  previewFileStatus: PreviewFileStatus;
}



export type ExportFormat = 'pdf' | 'docx';

export interface ExportStartResponse {
  success: boolean;
  message: string;
  reportId: number;
}

@Injectable({ providedIn: 'root' })
export class ReviewStateService {
  private http = inject(HttpClient);
  private runtimeConfigService = inject(RuntimeConfigService);

  private baseApiUrl = this.runtimeConfigService.apiUrl;
  private apiVersion = this.runtimeConfigService.apiVersion;

  // Sections come from the live API. Decisions, section saves, apply and the preview
  // conversions are still stubbed until those endpoints land (US-13-01, US-14-01, US-15-01,
  // US-16-01/02); flip this to false as each one is deployed.

  /** Append-only decision log — entries are never mutated or removed (US-13-02). */
  private readonly decisionLog: ReviewDecision[] = [];

  private getApiUrl(apiEndpoint: string): string {
    return buildEndpoint(this.baseApiUrl, this.apiVersion, 'reportservice', apiEndpoint);
  }

  /** Sections with their change candidates and provenance for the review screen (US-12-01). */
  getReviewSections(reportId: number): Observable<ReviewSection[]> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REVIEW.GetReview.replace('{reportId}', String(reportId)),
    );
    return this.http
      .get<ApiResponse<ReviewSectionDto[]>>(apiUrl)
      .pipe(map((response) => mapReviewSections(this.unwrap(response) ?? [])));
  }

  /**
   * Record an accept / decline / edit decision against a change candidate (US-13-01).
    * `editedText` is sent when the decision carries a replacement value.
   */
  recordDecision(
    reportId: number,
    request: RecordDecisionRequest,
    decidedBy: string,
  ): Observable<DecisionResult> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REVIEW.RecordDecision.replace('{reportId}', String(reportId)).replace(
        '{changeId}',
        String(request.changeId),
      ),
    );
    // The API's vocabulary is Accepted / Rejected / Edited; the UI says "Declined" throughout,
    // so the verb is translated here rather than leaking the API's wording into the screens.
    const decision = request.action === 'Declined' ? 'Rejected' : request.action;
    const shouldSendEditedText =
      request.action === 'Edited' ||
      (request.action === 'Accepted' && request.editedHtml !== null);
    const body = shouldSendEditedText
      ? { decision, editedText: request.editedHtml ?? '' }
      : { decision };

    return this.http.post<ApiResponse<DecisionResponseDto>>(apiUrl, body).pipe(
      map((response) => this.unwrap(response)),
      map((saved) => {
        const result: DecisionResult = {
          changeId: saved.changeId ?? saved.changed ?? request.changeId,
          reportId: saved.reportId ?? reportId,
          sectionId: saved.sectionId ?? request.sectionId,
          status: mapStatus(saved.status),
          editedHtml: saved.editedText,
          updatedDate: saved.updatedDate,
        };

        // Append-only log of what was decided in this session (US-13-02).
        this.decisionLog.push({
          reportId: result.reportId,
          sectionId: result.sectionId,
          changeId: result.changeId,
          action: request.action,
          editedHtml: result.editedHtml,
          decidedBy,
          decidedAtUtc: result.updatedDate || new Date().toISOString(),
        });

        return result;
      }),
    );
  }

  /**
   * Apply the accepted changes into a copy of the prior document (US-15-01 / US-15-02).
   * The server decides the set from the decisions already recorded, so nothing undecided can
   * be applied from here (US-15-03).
   */
  applyEdits(reportId: number): Observable<ApplyEditsResult> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REVIEW.ApplyEdits.replace('{reportId}', String(reportId)),
    );
    return this.http.post<ApiResponse<ApplyEditsResponseDto>>(apiUrl, {}).pipe(
      map((response) => this.unwrap(response)),
      map((data) => ({ reportId: data?.report_id ?? data?.reportId ?? reportId })),
    );
  }

  /**
   * Build the consolidated PDF or the updated .docx from the accepted changes
   * (US-18-01 / US-19-01). Same apply endpoint, selected by ?format=.
   */
  exportReport(reportId: number, format: ExportFormat): Observable<ExportStartResponse> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REVIEW.ApplyEdits.replace('{reportId}', String(reportId)),
    );
    return this.http.post<ApiResponse<{ reportId?: number; report_id?: number }>>(apiUrl, {}, { params: { format } }).pipe(
      map((response) => ({
        success: response.success,
        message: response.message,
        reportId: response.data?.report_id ?? response.data?.reportId ?? reportId,
      })),
    );
  }

  /** Progress and paths for the generated previews — polled until the status is Completed. */
  getPreviewFiles(reportId: number): Observable<PreviewFiles> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REVIEW.PreviewFiles.replace('{reportId}', String(reportId)),
    );
    return this.http
      .get<ApiResponse<PreviewFiles>>(apiUrl)
      .pipe(map((response) => this.unwrap(response)));
  }

  /**
   * Queue a fresh preview of the report (US-16-02). The response carries only the report id;
   * the rendered file is fetched afterwards with getReportPdf.
   */
  generateQuickPreview(reportId: number, includeAttachments = false): Observable<number> {
    const apiUrl = this.getApiUrl(
      API_ENDPOINTS.REPORT_SERVICE.REVIEW.QuickPreview.replace('{reportId}', String(reportId)),
    );
    return this.http.post<ApiResponse<ApplyEditsResponseDto>>(apiUrl, {}, {
      params: { attachments: includeAttachments },
    }).pipe(
      map((response) => this.unwrap(response)),
      map((data) => data?.report_id ?? data?.reportId ?? reportId),
    );
  }

  

  /** Read-only view of the decision log for this session. */
  getDecisionLog(reportId?: number): readonly ReviewDecision[] {
    return reportId == null
      ? [...this.decisionLog]
      : this.decisionLog.filter((entry) => entry.reportId === reportId);
  }

  private unwrap<T>(response: ApiResponse<T>): T {
    if (!response.success) {
      throw new Error(response.message);
    }
    return response.data;
  }
}
export { ReportPdfType };

